import idb from 'idb';
import { when } from 'mobx';
import ISyncErrorResponse from 'src/Interfaces/SyncErrorsInterface';
import { ErrorsStore } from 'src/Stores/Errors/Errors';
import { RootStore } from 'src/Stores/Root/RootStore';
import { IWellSyncError, IWellTestSyncError, SyncStore } from 'src/Stores/Sync/SyncStore';
import { ApiURL, useJWT } from '../../Constants';
import Auth from '../../Services/Auth';

const auth = new Auth();

export class API {

    private syncStore: SyncStore;
    private RootStore: RootStore;
    private errorsStore: ErrorsStore;
    public messageThrown: boolean = false;
    public apiUrl = ApiURL;
    public additionalHeaderObject: object;
    public MessageStore: any;
    constructor(rootStore: RootStore) {
        this.RootStore = rootStore;
        this.makeFetch = this.makeFetch.bind(this);
        this.syncData = this.syncData.bind(this);
    }

    public setInstanceStores() {
        this.syncStore = this.RootStore.SyncStore;
        this.errorsStore = this.RootStore.ErrorsStore;
        this.MessageStore = this.RootStore.MessageStore;
    }
    public get(url: string, headers?: any) {
        this.errorsStore.clearAll();

        return this.makeFetch(url, 'GET', undefined, headers);
    }

    public put(url: string, body: object, headers?: any) {
        this.errorsStore.clearAll();

        return this.makeFetch(url, 'PUT', body, headers);
    }

    public post(url: string, body: object, headers?: any) {
        this.errorsStore.clearAll();

        return this.makeFetch(url, 'POST', body, headers);
    }

    public file(url: string, body: object) {
        return this.makeFetch(url, 'FILE', body);
    }

    public delete(url: string) {
        return this.makeFetch(url, 'DELETE');
    }

    public login(url: string, body: object) {
        return this.makeFetch(url, 'POST', body, true);
    }

    public resetApiUrl() {
        this.apiUrl = ApiURL;
    }

    public setApiUrl(url: string) {
        this.apiUrl = url;
    }


    public checkStatus(response: any) {
        let error: any;

        if (response.status >= 200 && response.status < 300) {
            return response;
        } else if ( response.status === 401 ) {
            window.history.pushState(null, '/login');
        } else {
            error = new Error(response.statusText);
            error.response = response;
            throw error;
        }
    }

    public parseJSON(response: any) {
        return response.json();
    }

    public uniqueId() {
        return 'id-' + Math.random().toString(36).substr(2, 16);
    }

    public additionalHeaders(headers: object) {
        this.additionalHeaderObject = headers;
    }

    private async syncData() {
        let loginFailed = false;
        if (this.syncStore.tryingSync) {
            await when(() => !this.syncStore.tryingSync);
            if(this.RootStore.syncLoginFailed) {
                return true;
            }
        } else {
            this.syncStore.setTryingSync(true);
        }
        if (checkSync()) {
            const testOnlineRequest = new Request(this.apiUrl + '/synchronize', {method: 'GET'});
            const token = auth.getOnlineToken();
            testOnlineRequest.headers.set('authorization', 'Bearer ' + token);
            const response = await fetch(testOnlineRequest);

            if (response.ok) {
                this.RootStore.setSyncLoginFailed(false);
                this.MessageStore.addMessage('App paused starting offline data sync', 'warning');
                if(token) {
                    auth.setToken(token);
                }
                const result = await doSync();
                if (result && !result.success) {
                    if (result.errors.wellTestErrors) {
                        const wellTestErrors = result.errors.wellTestErrors;
                        this.syncStore.addUnsynedTests(wellTestErrors);
                    }
                }
                if (result) {
                    localStorage.setItem('needSync', 'false');
                    this.syncStore.syncComplete();
                }
                this.MessageStore.addMessage('App resumed data sync finished', 'warning');
            } else if (response.status === 401) {
                this.MessageStore.addMessage('Your session expired. Please log in.', 'warning');
                window.location.pathname = '/login';
                loginFailed = true;
                this.RootStore.setSyncLoginFailed(true);
            } else if (response.status === 404) {
                console.log('Still offline');
            }
        }
        this.syncStore.setTryingSync(false);
        return loginFailed;
    }

    public getFetchWithHeaders = async (url: string) => {
        const token = auth.getToken();
        const fetchObject: any = {
            headers: {
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            'importance' : 'low',
            'method': 'GET',
        };
        return fetch(this.apiUrl + url, fetchObject)
            .then(data => data.json())
            .then(data => data.data);
    }

    public getFileFetchWithHeaders = async (url: string) => {
        const token = auth.getToken();
        const fetchObject: any = {
            headers: {
                'Accept': 'application/octet-stream',
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/octet-stream',
            },
            'method': 'GET',
        };
        return fetch(this.apiUrl + url, fetchObject);
    }

    private async makeFetch(
        url: string, 
        method: string = 'GET', 
        body?: any,
        headers?: any
    ) {
        this.RootStore.addLoading();
        this.messageThrown = false;

        const fullURL = this.apiUrl + url;
        const notGetOrFile = ( method !== 'GET' && method !== 'FILE' );

        if ( !(url === '/account/login' || url === '/tenant' || url === '/' )) {
            const loc = window.location.pathname;
            localStorage.setItem('lastLocation', loc);
            if(await this.syncData()) {
                this.RootStore.removeLoading();
                return;
            }
        }
        
        
        let fetchObject: any = {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            'method': method,
        };

        if ( notGetOrFile ) {

            fetchObject.body = JSON.stringify(body);
        }

        if ( method === 'FILE' ) {
            let data = new FormData();
            if (body instanceof FormData) {
                data = body;
            } else {
                for (const prop in body) {
                    if (body.hasOwnProperty(prop)) {
                        data.append(prop, body[prop]);
                    }
                }
            }

            fetchObject = {
                body: data,
                headers: {
                    'Accept': 'application/json',
                },
                method: 'POST',
            };

        }

        if ( useJWT ) {
            const token = auth.getToken();
            fetchObject.headers.Authorization = 'Bearer ' + token;
        }

        if ( headers ) {
            for ( const prop in headers ) {
                if ( headers[prop] ) {
                    fetchObject.headers[prop] = headers[prop];
                }
            }
        }

        // Need to see what the location's pathname is
        const currentLocation = window.location.pathname;
        const location = currentLocation.indexOf('/login');

        return fetch(fullURL, fetchObject)
            .catch((error: any) => {
                // create the message
                let message: any = '';
                if ( error.status === 401 ) {
                    window.history.pushState(null, '/login');
                    return new Promise(
                        (res) => res
                    );
                }
                if ( error.status === 404 ) {
                    this.MessageStore.addMessage('<strong>API: \'' + url + '\'</strong> returned a 404 Not Found', 'warn');
                    return new Promise(
                        (res) => res
                    );
                }

                if ( error instanceof Object ) {
                    for ( const key in error ) {
                        if ( error[key] ) {
                            message += ' ' + key + ': ' + error[key];
                        }
                    }
                }

                if ( message === '' ) {
                    message = error.message;
                }

                // display the message
                this.MessageStore.addMessage('<strong>Url: \'' + url + '\'</strong>' + message, 'danger');

                this.messageThrown = true;

                return error;
            })
            .then(
                (response: any) => {
                    if (response.statusText === "OFFLINE") {
                        this.syncStore.setOffline(true);
                    } else {
                        this.syncStore.setOffline(false);
                    }
                    
                    if ( !response.ok && !this.messageThrown ) {

                        if ( response.status === 400 ) {
                            return this.handle400(response, url);
                        }

                        // User isn't authorized. Their session has expired.
                        // Redirect to Login if they aren't already there.
                        if ( response.status === 401 ) {

                            // If they are already on the Login page, we don't want to
                            // redirect a 401 since they are already on the Login page
                            if ( location < 0 && currentLocation !== '/' ) {
                                window.history.pushState({}, 'Not Authorized', '/login');
                                window.history.go();

                            } else if ( location > -1 || currentLocation === '/' ) {
                                this.MessageStore.addMessage('Invalid Login Credentials. Please try again.', 'danger');

                            } else {
                                this.MessageStore.addMessage('Unauthorized Request', 'danger');
                            }

                            // Return an empty Promise so component code doesn't fail
                            return new Promise(
                                (res) => res
                            );
                        }

                        if ( response.status === 403 ) {
                            this.MessageStore.addMessage('API: Not Authorized to view the resource at ' + url + ' Redirecting to homepage', 'warning', 1000);
                            setTimeout(() => window.location.href = '/home', 1000);
                            return undefined;
                        }

                        if ( response.status === 404 ) {
                            this.MessageStore.addMessage('<strong>API: \'' + url + '\'</strong> returned a 404 Not Found', 'warn');
                            return undefined;
                        }

                        if ( response.Message ) {
                            this.MessageStore.addMessage(response.Message, 'danger');
                        }

                        return response.json()
                            .then(
                                (data: any) => {
                                    // display the message
                                    for (const error in data.errors) {
                                        if( data.errors[error]) {
                                            this.MessageStore.addMessage(data.errors[error], 'danger');
                                        }
                                    }
                                    // throw Error(error);
                                }
                            );

                    // Normal server codes returned, but still need to check the JSON returned
                    // Returned JSON could have a Success property set to false
                    } else {
                        if (response.headers) {
                            if (response.headers.get('Set-Authorization')) {
                                auth.setToken(response.headers.get('Set-Authorization'));
                                if (!this.RootStore.SyncStore.offline) {
                                    auth.setOnlineToken(response.headers.get('Set-Authorization'));
                                }
                                this.RootStore.UserStore.getPermissions();
                            }
                        }

                        if ( typeof response === 'object' && response.message ) {
                            this.MessageStore.addMessage(response.message, 'danger');
                            throw response;
                        } else {
                            return this.parseResponse(response, url);
                        }
                    }
            })
            .catch((error) => {
                this.MessageStore.addMessage("Your submitted data was not saved.", 'danger');
            })
            .then(data => {
                this.RootStore.removeLoading();
                return data;
            });
    }

    private handle400(response: any, url: any) {
        return response.json()
            .then((body: any) => {
                if ( body.Message ) {
                    this.MessageStore.addMessage(body.Message, 'danger');
                }
                
                if ( body.Errors ) {
                    this.errorsStore.parse(body.Errors);
                }
                throw new Error("A server error has occurred");
            });
    }

    private parseResponse(response: any, url: any) {

        return response.json()
            .then(
                (body: any) => {
                    // Test if the Success property is set to false
                    // If it is, display a generic message and throw an Error
                    if (
                        body &&
                        (body.hasOwnProperty('success') && !body.success)
                    ) {
                        for( const error in body.errors) {
                            if(body[error]) {
                                this.MessageStore.addMessage(body.errors[error], 'danger');
                            }
                        }

                        throw Error('An Error Has Occurred');
                    }

                    if ( body.Message ) {
                        this.MessageStore.addMessage(body.Message, 'danger');
                    }

                    if ( body.data === undefined ) {
                        this.MessageStore.addMessage(
                            'The API <strong>\'' +
                            url +
                            '\'</strong> ' +
                            'did not return the proper body format: ' +
                            '<strong>Missing `data` property in the body</strong>',
                            'warn'
                        );

                        return new Promise(
                            (res) => res
                        );
                    }

                    return body.data;
                }
            );

    }
}


async function doSync() {
    const dirtyWellData = await getDirtyData( 'well-data' );
    const dirtyWelltestsData = await getDirtyData( 'wellTest-data' );
    if (dirtyWellData.length === 0 && dirtyWelltestsData.length === 0) {
      console.log("No well Data to sync.");
      localStorage.setItem('needSync', 'false');
      return { success: true };
    }
  
    const currToken = auth.getToken();
    const packet = {
      wellTests: dirtyWelltestsData,
      wells: dirtyWellData,
    };
  
    console.log( "Data to be sent: ", packet );
    const url = ApiURL + '/synchronize';
    const syncReq = new Request(url, {
        body: JSON.stringify(packet), 
        headers: { 
            'authorization': 'Bearer ' + currToken,
            'content-type' : 'application/json',
        },
        method: 'POST', 
    });

    const response = await fetch(syncReq)
      .catch(error => {
        console.log('Error in sync ', error);
        return undefined;
      });
    if (!response) {
        return;
    }
        
    if(response.status === 200) {
        clearDirtyData('well-data');
        clearDirtyData('wellTest-data');
        clearDirtyData('wellTest-list');
        clearDirtyData('wellTest-error');
        return { success: true };
    } else if (response.status === 409) {
        const respJson = await response.json();
        if (respJson) {
            await removeSavedData(respJson.data.savedWells, respJson.data.savedTests);
            const syncErrors: ISyncErrorResponse = {
                wellErrors: respJson.data.wellErrors,
                wellTestErrors: respJson.data.wellTestErrors,
            };
            return { success: false, errors: syncErrors };
        }
    } else if (response.status === 400) {
        const respJson = await response.json();
        console.log(respJson);
        const errorList = respJson.Errors;
        const syncErrorsUpdate: any[] = [];
        const syncErrorsWell: any[] = [];

        Object.keys(errorList).forEach((element: any) => {
            const parsedKey: string[] = element.split('.');
            if(parsedKey[0] && parsedKey[0].indexOf("WellTests") >= 0) {
                const indexArr = parsedKey[0].match(/\[(.*)\]/);
                if (indexArr) {
                    console.log(indexArr);
                    const index = Number(indexArr[1]);
                    addError(syncErrorsUpdate, index, { 
                        errorList: errorList[element],
                        path: parsedKey.slice(1).join('.'),
                    });
                    console.log(syncErrorsUpdate);
                }
            } else if(parsedKey[0] && parsedKey[0].indexOf("Wells") >= 0) {
                const indexArr = parsedKey[0].match(/\[(.*)\]/);
                if (indexArr) {
                    const index = Number(indexArr[1]);
                    addError(syncErrorsWell, index, { 
                        errorList: errorList[element],
                        path: parsedKey.slice(1).join('.'),
                    });
                }
            }
        });

        const wellTestSyncErrors: IWellTestSyncError[] = [];
        const wellSyncErrors: IWellSyncError[] = [];
        console.log(syncErrorsUpdate);
        packet.wellTests.forEach((value, index) => {
            let myErrorMessage = '';
            if (syncErrorsUpdate[index]) {
                syncErrorsUpdate[index].forEach((errObj: any) => {
                    myErrorMessage += errObj.path + ': ' + errObj.errorList.join('\n') + '\n';
                });
            }
            if (myErrorMessage === '') {
                myErrorMessage = 'No Errors in this Test. Failed because of errors in other synced tests.';
            }
            const testError: IWellTestSyncError = {
                errorMessage: myErrorMessage,
                testDate: value.testDate,
                wellId: value.wellId,
                wellTestId: value.id,
            };
            wellTestSyncErrors.push(testError);
        });

        packet.wells.forEach((value, index) => {
            let myErrorMessage = '';
            if (syncErrorsWell[index]) {
                syncErrorsWell[index].forEach((errObj: any) => {
                    myErrorMessage += errObj.path + ': ' + errObj.errorList.join('\n') + '\n';
                });
            }
            if (myErrorMessage === '') {
                myErrorMessage = 'No Errors in this Well. Failed because of errors in other synced tests.';
            }
            const wellError: IWellSyncError = {
                errorMessage: myErrorMessage,
                wellId: value.Id,
            };
            wellSyncErrors.push(wellError);
        });
        const syncErrors: ISyncErrorResponse = {
            wellErrors: wellSyncErrors,
            wellTestErrors: wellTestSyncErrors,
        };
        return { success: false, errors: syncErrors};

    }
    let errorRespJson;
    try {
        errorRespJson = await response.json();
    } catch (e) {
        errorRespJson = undefined;
    }
    return { success: false, errors: errorRespJson ? errorRespJson.data.errors : [] };
    
}

async function clearDirtyData(storeName: any ) {
    const db = await idb.open('dirty-data-store', 1, upgradeDB => {
        createDatabase(upgradeDB, false);
    });
    const remTx = db.transaction(storeName, 'readwrite').objectStore(storeName);
    await remTx.clear();
}
async function removeSavedData(savedWells: string[], savedTests: string[]) {
    const db = await idb.open('dirty-data-store', 1, upgradeDb => {
        createDatabase(upgradeDb, false);
    });
    const remUpdatedTest = db.transaction('wellTest-data', 'readwrite').objectStore('wellTest-data');
    savedTests.forEach(async(value) => {
        await remUpdatedTest.delete(value);
    });
    const remWells = db.transaction('well-data', 'readwrite').objectStore('well-data');
    savedWells.forEach(async(value) => {
        await remWells.delete(value);
    });
    
}
function checkSync(): boolean {
    if(localStorage.getItem('needSync') === 'true') {
        return true;
    }

    return false;
}

async function getDirtyData( dirtyStore:any ) {
    const db = await idb.open('dirty-data-store', 1, upgradeDB => {
      createDatabase(upgradeDB, false);
    });
    const tx = db.transaction(dirtyStore ).objectStore( dirtyStore );
    const data = await tx.getAll();
    return data;
}

function createDatabase(upgradeDb:any , trueIfClean: any) {
    upgradeDb.createObjectStore('wellTest-list');
    upgradeDb.createObjectStore('wellTest-data');
    upgradeDb.createObjectStore('well-list');
    upgradeDb.createObjectStore('well-data');
    if (trueIfClean) {
      upgradeDb.createObjectStore('static-other');
    } else {
      upgradeDb.createObjectStore('wellTest-error');
    }
}

function addError(errArr: any, index:any, errEle:any) {
    console.log(errArr, errEle);
    if(Array.isArray(errArr[index])) {
        errArr[index].push(errEle);
    } else {
        errArr[index] = [errEle];
    }
    console.log(errArr, errEle);
}



// WEBPACK FOOTER //
// ./src/Services/Api/Api.tsx